import type { StatusIndicatorProps } from '@workday/canvas-kit-preview-react/status-indicator';
import { UserBase } from 'shared/models/user';
import { getEnumValues } from 'modules/common/util/ObjectUtil';
import { OrgPermission } from './permissions';

export enum CompanySource {
  WCP = 'WCP',
  SFDC = 'SFDC',
}

export interface OrganizationBase {
  organizationName: string;
  organizationUuid: string;
  organizationShortId: string;
  associationType?: CompanyAssociationTypes;
}

// schema returned from /v2/users/self/organizations
export interface UserCompanyBase extends OrganizationBase {
  region: string;
  organizationType: CompanyTypes;
}

// schema returned from /v2/organizations/self
export interface UserCompany extends UserCompanyBase {
  tenantPrefix: string;
  roles: string[];
  isAWS: boolean;
}

/**
 * @deprecated Use `UserCompany` instead
 * DO NOT USE THE FOLLOWING MODEL (UserOrganization)
 * It exists only to support OctoPaasEndpoint.USER_ORGS, which should be avoided.
 */
export interface UserOrganization extends OrganizationBase {
  isEmployee: boolean;
  organizationPermissions: OrgPermission[];
  roles: CompanyRole[];
  isAWS: boolean;
}

export interface RawCompany {
  uuid: string;
  name: string;
  organizationType: string;
  usersTotal?: number;
  region: string;
  developerTenantsAllowed?: number;
  source?: CompanySource;
  billingId?: string;
  shortId?: string;
  emailDomains?: string[];
  tenantPrefix?: string;
  mainOrgIsChild?: boolean;
}

export interface OrganizationAssociation {
  parentOrgUuid: string;
  childOrgUuid: string;
  source: string;
  createdBy: string;
  createdOn: number;
}

export interface RawCompanyWithAssociations extends RawCompany {
  association?: OrganizationAssociation;
}

export interface CreateCompanyRequest {
  name: string;
  organizationType: string;
  region: string;
  billingId?: string;
  emailDomains?: string[];
  tenantPrefix?: string;
  admins: {
    email: string;
    firstName: string;
    lastName: string;
    userExists: boolean;
  }[];
  developerEmails: string[];
}

export interface CreateCompanyResponse extends RawCompany {
  adminEmails: string[];
  developerEmails: string[];
}
export interface CompanyUser extends UserBase {
  userEmail: string;
  userUuid: string;
  isDsc?: boolean;
  roles?: CompanyRole[];
  associationType?: CompanyAssociationTypes;
  isAWS?: boolean;
}

export interface CompanyUserId extends CompanyUser {
  id: number;
}

export interface DetailCompanyUser extends CompanyUser {
  lastLogin?: number;
}

export interface NumericalEntitlement {
  value: number;
  overridden: boolean;
}

export interface StringEntitlement {
  value: string;
  overridden: boolean;
}

export interface Entitlements {
  aiUsageLimit?: NumericalEntitlement;
  userAcctAllowed: NumericalEntitlement;
  dataLimitInGb?: NumericalEntitlement;
  developerSupportContactsAllowed: NumericalEntitlement;
  requestsPerSecondLimit: NumericalEntitlement;
  wcpAgreementStartDate: StringEntitlement;
  developerTenantsAllowed: NumericalEntitlement;
  customerProductionDataCenterRegion?: StringEntitlement;
  governmentCloudIncluded?: StringEntitlement;
  totalProdAppsAllowed?: NumericalEntitlement;
  wcpAgreementEndDate?: StringEntitlement;
  awsUserAccountLimit?: NumericalEntitlement;
}

export interface CompanySaml {
  displayName: string;
  entityId: string;
  domainName: string;
  nameIdFormat: string;
  encodedPublicKey: string;
  signonEndpoint: string;
  signoutEndpoint: string;
  signType?: string;
  compressedAuthNRequest?: boolean;
}

export interface CompanySamlToggle {
  toggleStatus: string;
  verified: boolean;
}

export interface CompanySamlEndpoints {
  ssoUrl: string;
  sloUrl: string;
  publicKeyCertificate: string;
}

// Packages listed here are referenced in our code AND/OR another team.
// Ex.: Automation onboards new companies that purchase Extend from Salesforce into Octopaas and assign the appropriate packages.
// Being listed here indicates to users in the Admin Tools UI that the exact naming is important
// or that their configuration is managed by automation in https://ghe.megaleo.com/wcp/wcp-access-policies
export enum KnownPackages {
  JOURNEYS = 'Journeys', // may be referenced by automation
  EXTEND_BASIC = 'extendbasic', // may be referenced by automation
  EXTEND_ESSENTIALS = 'extendessentials', // may be referenced by automation
  EXTEND_PRO = 'extendpro', // may be referenced by automation
  EXTEND_SAML = 'extendsaml',
  INTEGRATIONS = 'Orchestrate for Integrations', // may be referenced by automation
  ML_APIS_EA = 'ML APIs Early Adopter Program',
  ML_APIS_LABS = 'ML APIs Labs Program',
  DIST_APPS_PRODUCER = 'DistAppProducer',
  DIST_APPS_CONSUMER = 'DistAppConsumer',
  HACKATHON = 'Hackathon',
  ORCH_MANAGEMENT = 'Orch Management',
  ORCH_MANAGEMENT_SUMMARY = 'Orchestrate Management Summary Report',
  ORCH_ASYNC_LIFESTYLE = 'OrchestrateAsyncLifecycle',
}

export const KNOWN_PACKAGES = getEnumValues(KnownPackages);

export enum CompanyTypes {
  CUSTOMER = 'Customer',
  EDUCATION = 'Education',
  HACKATHON = 'Hackathon',
  INTERNAL = 'Internal',
  PARTNER = 'Partner',
  SOFTWARE_PARTNER = 'Software Partner',
  PRODUCER = 'Producer',
}

export enum CompanyAssociationTypes {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  COLLABORATOR = 'COLLABORATOR',
}

export const COMPANY_TYPES = getEnumValues(CompanyTypes);

export enum CompanyRole {
  MEMBER = 'member',
  SPECIALIST = 'specialist',
  DEVELOPER = 'developer',
  ADMIN = 'admin',
  DSC = 'dsc',
  SYSTEM_ACCOUNT = 'systemaccount',
}

export const CompanyDisplayRole: Record<CompanyRole, string> = {
  [CompanyRole.MEMBER]: 'Member',
  [CompanyRole.SPECIALIST]: 'Specialist',
  [CompanyRole.DEVELOPER]: 'Developer',
  [CompanyRole.ADMIN]: 'Admin',
  [CompanyRole.DSC]: 'Developer Support Contact',
  [CompanyRole.SYSTEM_ACCOUNT]: 'System User',
};

/** Narrows a raw role string, such as one from `UserCompany.roles`, to a known `CompanyRole`. */
export const isCompanyRole = (role: string): role is CompanyRole =>
  Object.values(CompanyRole).some((knownRole) => knownRole === role);

/** `CompanyDisplayRole` with the short "DSC" label, for tight spaces like column headings and menus. */
export const SimpleCompanyDisplayRole: Record<CompanyRole, string> = {
  ...CompanyDisplayRole,
  [CompanyRole.DSC]: 'DSC',
};

export enum CompanyRoleDisplay {
  PARTNER = 'Partner Contact',
}

export interface CompanyAwsAccount {
  accountStatus: CompanyAwsStatus;
  accountName: string;
  deletionDate?: string;
}

export enum CompanyAwsStatus {
  INITIATED = 'INITIATED',
  PROVISIONING = 'PROVISIONING',
  PROVISIONED = 'PROVISIONED',
  DELETION_REQUESTED = 'DELETION_REQUESTED',
  DELETION_SCHEDULED = 'DELETION_SCHEDULED',
  ERROR_APES = 'ERROR_APES',
  EMPTY = '',
}

export const CompanyAwsStatusIndicatorLabel: Record<CompanyAwsStatus, string> = {
  [CompanyAwsStatus.INITIATED]: 'Initiated',
  [CompanyAwsStatus.PROVISIONING]: 'Provisioning',
  [CompanyAwsStatus.PROVISIONED]: 'Active',
  [CompanyAwsStatus.DELETION_REQUESTED]: 'Deletion Requested',
  [CompanyAwsStatus.DELETION_SCHEDULED]: 'Deletion Scheduled',
  [CompanyAwsStatus.ERROR_APES]: 'Error',
  [CompanyAwsStatus.EMPTY]: 'Not Active',
};

export const CompanyAwsStatusIndicatorVariant: Record<CompanyAwsStatus, StatusIndicatorProps['variant']> = {
  [CompanyAwsStatus.INITIATED]: 'caution',
  [CompanyAwsStatus.PROVISIONING]: 'info',
  [CompanyAwsStatus.PROVISIONED]: 'positive',
  [CompanyAwsStatus.DELETION_REQUESTED]: 'critical',
  [CompanyAwsStatus.DELETION_SCHEDULED]: 'critical',
  [CompanyAwsStatus.ERROR_APES]: 'critical',
  [CompanyAwsStatus.EMPTY]: 'neutral',
};

export const CompanyAwsStatusDescription: Record<CompanyAwsStatus, string> = {
  [CompanyAwsStatus.INITIATED]: 'Organization account creation initiated.',
  [CompanyAwsStatus.PROVISIONING]: 'Organization account provisioning.',
  [CompanyAwsStatus.PROVISIONED]: 'Organization account provisioned.',
  [CompanyAwsStatus.DELETION_REQUESTED]: 'Organization account provisioned.',
  [CompanyAwsStatus.DELETION_SCHEDULED]: 'Organization account provisioned.',
  [CompanyAwsStatus.ERROR_APES]: 'Organization account creation failed. Please reach out to Workday support.',
  [CompanyAwsStatus.EMPTY]: 'Organization account not provisioned.',
};
